import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bookmark,
  BookmarkPlus,
  ChevronDown,
  Check,
  Trash2,
  Star,
  Loader2,
  X,
} from "lucide-react";
import { listSavedViews, createSavedView, deleteSavedView, SavedView } from "../../api/client";
import { FilterState } from "./FilterBar";

interface SavedViewsDropdownProps {
  repoId: string;
  currentFilters: FilterState;
  onApplyView: (filters: FilterState) => void;
}

export default function SavedViewsDropdown({
  repoId,
  currentFilters,
  onApplyView,
}: SavedViewsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPos, setPanelPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const qc = useQueryClient();

  // Position the portal-rendered panel below the trigger button
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 8,
      left: rect.right - 288, // w-72 = 18rem = 288px, right-aligned
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
  }, [isOpen, updatePosition]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  // Fetch saved views
  const viewsQuery = useQuery({
    queryKey: ["savedViews"],
    queryFn: () => listSavedViews(),
  });

  const views = viewsQuery.data || [];

  // Create view mutation
  const createMutation = useMutation({
    mutationFn: (data: { name: string; is_default: boolean }) =>
      createSavedView({
        name: data.name,
        filters: {
          severities: currentFilters.severities,
          statuses: currentFilters.statuses,
          kinds: currentFilters.kinds,
          tools: currentFilters.tools,
          search: currentFilters.search,
        },
        is_default: data.is_default,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["savedViews"] });
      setShowSaveModal(false);
      setNewViewName("");
      setIsDefault(false);
    },
  });

  // Delete view mutation
  const deleteMutation = useMutation({
    mutationFn: (viewId: string) => deleteSavedView(viewId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["savedViews"] });
    },
  });

  const handleApplyView = (view: SavedView) => {
    onApplyView({
      search: view.filters.search || "",
      severities: (view.filters.severities || []) as any,
      statuses: (view.filters.statuses || []) as any,
      kinds: (view.filters.kinds || []) as any,
      tools: view.filters.tools || [],
    });
    setIsOpen(false);
  };

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    createMutation.mutate({ name: newViewName.trim(), is_default: isDefault });
  };

  // Preset views
  const presetViews = [
    { name: "Critical & High", filters: { severities: ["critical", "high"], statuses: ["open"] } },
    { name: "Open Issues", filters: { severities: [], statuses: ["open"] } },
    { name: "Fixed", filters: { severities: [], statuses: ["fixed"] } },
    { name: "Secrets Only", filters: { kinds: ["secret"], statuses: ["open"] } },
    { name: "Dependency Vulnerabilities", filters: { kinds: ["sca"], statuses: ["open"] } },
  ];

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className="btn-secondary flex items-center gap-2"
      >
        <Bookmark className="h-4 w-4" />
        Views
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && createPortal(
        <>
          {/* Backdrop to ensure dropdown is clearly above everything */}
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: panelPos.top,
              left: panelPos.left,
              zIndex: 9999,
            }}
            className="w-72 max-h-[80vh] overflow-y-auto rounded-lg shadow-2xl border border-border ring-1 ring-black/10 animate-fade-in scrollbar-thin"
          >
            {/* Solid background wrapper to prevent see-through */}
            <div style={{ background: "rgb(var(--surface-2))" }} className="rounded-lg">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border bg-surface3/60">
            <div className="text-sm font-semibold text-text">Saved Views</div>
            <div className="text-xs text-text2 mt-0.5">Quick filters for common queries</div>
          </div>

          {/* Preset views */}
          <div className="p-2">
            <div className="text-[11px] font-semibold text-text2 uppercase tracking-wider px-2 py-1.5">
              Presets
            </div>
            {presetViews.map((preset) => (
              <button
                key={preset.name}
                onClick={() => {
                  onApplyView({
                    search: "",
                    severities: (preset.filters.severities || []) as any,
                    statuses: (preset.filters.statuses || []) as any,
                    kinds: (preset.filters.kinds || []) as any,
                    tools: [],
                  });
                  setIsOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm text-text hover:bg-surface3 rounded-md transition-colors flex items-center gap-2"
              >
                <span className="flex-1">{preset.name}</span>
              </button>
            ))}
          </div>

          {/* Saved views */}
          {views.length > 0 && (
            <div className="p-2 border-t border-border">
              <div className="text-[11px] font-semibold text-text2 uppercase tracking-wider px-2 py-1.5">
                Your Views
              </div>
              {views.map((view) => (
                <div
                  key={view.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-surface3 rounded-md group"
                >
                  <button
                    onClick={() => handleApplyView(view)}
                    className="flex-1 text-left text-sm text-text flex items-center gap-2"
                  >
                    {view.is_default && <Star className="h-3 w-3 text-brand fill-brand" />}
                    {view.name}
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(view.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-critical/20 rounded transition-all"
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-critical" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Save current view */}
          <div className="p-2 border-t border-border">
            <button
              onClick={() => setShowSaveModal(true)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand hover:bg-brand/10 rounded-md transition-colors"
            >
              <BookmarkPlus className="h-4 w-4" />
              Save Current Filters
            </button>
          </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Save View Modal */}
      {showSaveModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowSaveModal(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-surface1 rounded-lg shadow-xl border border-divider z-50 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text">Save View</h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="btn-icon"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text2 mb-1">
                  View Name
                </label>
                <input
                  type="text"
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  placeholder="e.g., Critical Production Issues"
                  className="input w-full"
                  autoFocus
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-text2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="rounded border-divider"
                />
                Set as default view
              </label>

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveView}
                  disabled={!newViewName.trim() || createMutation.isPending}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {createMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
